// Spec 078 — hooks da biblioteca de modelos 3D. Invalidação explícita por mutação (tabela em
// contracts/scene-ui-contract.md → invalidacao-de-cache.mdx). O upload usa XMLHttpRequest dentro
// do useMutation para ter progresso (FR-028); o erro vira ApiClientError como no api-client.
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { api, ApiClientError } from "@/lib/api-client";
import type { MachineDetail } from "@/features/lines/hooks";
import type {
  Model3dDetail,
  Model3dSummary,
  UpdateModel3dRequest,
  UploadProgress,
} from "@/types/models-3d.types";

export const models3dKey = ["models-3d"] as const;
export const model3dKey = (id: string) => ["models-3d", id] as const;

export function useModels3d(): UseQueryResult<Model3dSummary[]> {
  return useQuery({
    queryKey: models3dKey,
    queryFn: () => api.get<Model3dSummary[]>("/api/v1/models-3d"),
  });
}

export function useModel3d(id: string | null): UseQueryResult<Model3dDetail> {
  return useQuery({
    queryKey: model3dKey(id ?? ""),
    queryFn: () => api.get<Model3dDetail>(`/api/v1/models-3d/${id}`),
    enabled: Boolean(id),
  });
}

interface UploadArgs {
  file: File;
  name?: string;
  onProgress?: (p: UploadProgress) => void;
}

/** PUT/POST multipart com progresso. Resolve com o envelope `data`; rejeita com ApiClientError. */
function xhrUpload<T>(method: "POST" | "PUT", url: string, args: UploadArgs): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const form = new FormData();
    if (args.name) form.append("name", args.name);
    form.append("file", args.file, args.file.name);
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    xhr.withCredentials = true;
    const token = api.getAccessToken?.();
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        args.onProgress?.({
          loadedBytes: e.loaded,
          totalBytes: e.total,
          pct: (e.loaded / e.total) * 100,
        });
      }
    };
    xhr.onerror = () => reject(new Error("Não foi possível enviar o arquivo. Tente novamente."));
    xhr.onload = () => {
      let body: {
        data?: T;
        error?: { code: string; message: string; details?: Record<string, unknown> } | null;
      } = {};
      try {
        body = JSON.parse(xhr.responseText) as typeof body;
      } catch {
        // resposta sem JSON: cai no erro genérico abaixo
      }
      if (xhr.status >= 200 && xhr.status < 300 && body.data !== undefined)
        return resolve(body.data);
      const err = body.error ?? {
        code: "UPLOAD_FAILED",
        message: "Não foi possível enviar o arquivo. Tente novamente.",
      };
      reject(new ApiClientError(xhr.status, err as never));
    };
    xhr.send(form);
  });
}

export function useUploadModel3d() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: UploadArgs) => xhrUpload<Model3dDetail>("POST", "/api/v1/models-3d", args),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: models3dKey });
      void qc.invalidateQueries({ queryKey: ["scene"] });
    },
    meta: { skipErrorToast: true },
  });
}

export function useReplaceModel3dFile(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: UploadArgs) =>
      xhrUpload<Model3dDetail>("PUT", `/api/v1/models-3d/${id}/file`, args),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: models3dKey });
      void qc.invalidateQueries({ queryKey: ["scene"] });
    },
    meta: { skipErrorToast: true },
  });
}

export function useUpdateModel3d(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateModel3dRequest) =>
      api.patch<Model3dDetail>(`/api/v1/models-3d/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: models3dKey });
      void qc.invalidateQueries({ queryKey: ["scene"] });
    },
  });
}

export function useDeleteModel3d() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; confirmationName: string }) =>
      api.delete<void>(`/api/v1/models-3d/${args.id}`, { confirmationName: args.confirmationName }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: models3dKey });
      void qc.invalidateQueries({ queryKey: ["scene"] });
      void qc.invalidateQueries({ queryKey: ["lines"] });
    },
    meta: { skipErrorToast: true },
  });
}

/** Associa (ou desassocia com null) o modelo a uma máquina — PATCH da máquina. */
export function useAssignModel3d() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { lineId: string; machineId: string; model3dId: string | null }) =>
      api.patch<MachineDetail>(`/api/v1/lines/${args.lineId}/machines/${args.machineId}`, {
        model3dId: args.model3dId,
      }),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ["lines", vars.lineId, "machines"] });
      void qc.invalidateQueries({ queryKey: ["scene", vars.lineId] });
      void qc.invalidateQueries({ queryKey: models3dKey });
    },
  });
}
